import { ShapePointViewModel } from '../viewModels/shapePointViewModel';
import { MarkerType } from 'src/app/modules/shared/enums/maps/marketType';
import { ZonesAreasUtilies } from './zones.utilities';
import { IconUtilities } from './icon.utilities';
import { MapConstants } from '../../settings/constants/mapLayers';
import { LevelIndexLabelViewModel } from '../../settings/viewModels/levelIndexLabelViewModel';
import { ObjectTypes } from '../enums/objectTypes';
import { IncidentItemTypes } from '../../incidents/enums/incidentItemTypes';
import { RiskTypes } from '../../risk/enums/riskTypes';
import { GeoBounds } from '../interfaces/map/bounds.interface';
import { GeoPoint } from '../interfaces/map/position.interface';

export class GMapUtilities {
  public static readonly DEFAULT_RUNSHEET_ITEM_COLOR = '#4EC3E0';
  public static readonly DEFAULT_GRAY_BUTTON_COLOR = 'rgb(86, 86, 86)';
  public static readonly DEFAULT_ZONE_POLYGON_COLOR = '#4EC3E0';
  public static readonly DEFAULT_AREA_POLYGON_COLOR = '#F87C56';

  public static getMapFocusStyle(controlLabel: string): google.maps.StyledMapType {
    return new google.maps.StyledMapType(
      [
        {
          elementType: 'geometry',
          stylers: [
            {
              color: '#f5f5f5',
            },
          ],
        },
        {
          elementType: 'labels.icon',
          stylers: [
            {
              visibility: 'off',
            },
          ],
        },
        {
          elementType: 'labels.text.fill',
          stylers: [
            {
              color: '#616161',
            },
          ],
        },
        {
          elementType: 'labels.text.stroke',
          stylers: [
            {
              color: '#f5f5f5',
            },
          ],
        },
        {
          featureType: 'administrative.land_parcel',
          elementType: 'labels.text.fill',
          stylers: [
            {
              color: '#bdbdbd',
            },
          ],
        },
        {
          featureType: 'poi',
          elementType: 'geometry',
          stylers: [
            {
              color: '#eeeeee',
            },
          ],
        },
        {
          featureType: 'poi',
          elementType: 'labels.text.fill',
          stylers: [
            {
              color: '#757575',
            },
          ],
        },
        {
          featureType: 'poi.park',
          elementType: 'geometry',
          stylers: [
            {
              color: '#e5e5e5',
            },
          ],
        },
        {
          featureType: 'poi.park',
          elementType: 'labels.text.fill',
          stylers: [
            {
              color: '#9e9e9e',
            },
          ],
        },
        {
          featureType: 'road',
          elementType: 'geometry',
          stylers: [
            {
              color: '#ffffff',
            },
          ],
        },
        {
          featureType: 'road.arterial',
          elementType: 'labels.text.fill',
          stylers: [
            {
              color: '#757575',
            },
          ],
        },
        {
          featureType: 'road.highway',
          elementType: 'geometry',
          stylers: [
            {
              color: '#dadada',
            },
          ],
        },
        {
          featureType: 'road.highway',
          elementType: 'labels.text.fill',
          stylers: [
            {
              color: '#616161',
            },
          ],
        },
        {
          featureType: 'road.local',
          elementType: 'labels.text.fill',
          stylers: [
            {
              color: '#9e9e9e',
            },
          ],
        },
        {
          featureType: 'transit.line',
          elementType: 'geometry',
          stylers: [
            {
              color: '#e5e5e5',
            },
          ],
        },
        {
          featureType: 'transit.station',
          elementType: 'geometry',
          stylers: [
            {
              color: '#eeeeee',
            },
          ],
        },
        {
          featureType: 'water',
          elementType: 'geometry',
          stylers: [
            {
              color: '#c9c9c9',
            },
          ],
        },
        {
          featureType: 'water',
          elementType: 'labels.text.fill',
          stylers: [
            {
              color: '#9e9e9e',
            },
          ],
        },
      ],
      {
        name: controlLabel,
        maxZoom: 22,
      }
    );
  }

  public static getMapLayers(): LevelIndexLabelViewModel[] {
    const mapLayers: LevelIndexLabelViewModel[] = [];

    for (let i = 0; i < MapConstants.MAX_MAP_LAYERS; i++) {
      mapLayers.push({
        id: `${i}`,
        label: `${i}`,
      } as LevelIndexLabelViewModel);
    }

    return mapLayers;
  }

  public static getMarkerTypeFromObjectType(objectType: ObjectTypes, subType: RiskTypes | IncidentItemTypes = null): MarkerType {
    let markerType = MarkerType.PROJECT;

    switch (objectType) {
      case ObjectTypes.Project:
        markerType = MarkerType.PROJECT;
        break;
      case ObjectTypes.IncidentItem:
        switch(subType) {
          case IncidentItemTypes.Incident:
            markerType = MarkerType.INCIDENT;
            break;
          case IncidentItemTypes.Log:
            markerType = MarkerType.LOG;
            break;
          case IncidentItemTypes.Job:
            markerType = MarkerType.JOB;
            break;
          default:
            markerType = MarkerType.INCIDENT;
            break;
        }
        markerType = MarkerType.INCIDENT;
        break;
      case ObjectTypes.Job:
        markerType =  MarkerType.JOB;
        break;
      case ObjectTypes.Runsheet:
        markerType = MarkerType.RUNSHEET_ITEM;
        break;
      case ObjectTypes.Runsheet_Item:
        markerType = MarkerType.RUNSHEET_ITEM;
        break;
      case ObjectTypes.Task_Group:
        markerType = MarkerType.TASK_GROUP;
        break;
      case ObjectTypes.Risk:
        switch (subType) {
          case RiskTypes.Risk:
            markerType = MarkerType.RISK;
            break;
          case RiskTypes.Issue:
            markerType = MarkerType.ISSUE;
            break;
          case RiskTypes.Opportunity:
            markerType = MarkerType.OPPORTUNITY;
            break;
        }
        break;
      default:
        markerType = MarkerType.TASK;
        break;
    }

    return markerType;
  }

  public static convertLatLngBoundsToPolygonPath(
    latLngBounds: google.maps.LatLngBounds
  ): google.maps.MVCArray<google.maps.MVCArray<google.maps.LatLng>> {
    const paths = new google.maps.MVCArray<google.maps.MVCArray<google.maps.LatLng>>();
    const path = new google.maps.MVCArray<google.maps.LatLng>();
    const ne = latLngBounds.getNorthEast();
    const sw = latLngBounds.getSouthWest();
    path.push(ne);
    path.push(new google.maps.LatLng(sw.lat(), ne.lng()));
    path.push(sw);
    path.push(new google.maps.LatLng(ne.lat(), sw.lng()));
    paths.push(path);

    return paths;
  }

  //@Deprecated
  public static createZoneAreaThreeStateButton(options: any): HTMLDivElement {
    const zonesControlUI = document.createElement('div');
    zonesControlUI.style.width = '26px';
    zonesControlUI.style.marginLeft = '-2px';
    zonesControlUI.className = 'map-control-btn';
    zonesControlUI.title = options.title;

    const zonesControlIcon = document.createElement('i');
    zonesControlIcon.className = options.initialClassName;
    zonesControlUI.appendChild(zonesControlIcon);
    zonesControlUI.addEventListener('click', options.onClick);

    return zonesControlUI;
  }

  public static buildMarkerSVG(markerType: MarkerType, rgbColor: string): string {
    return IconUtilities.buildMarkerSVG(markerType, rgbColor);
  }

  public static deg2rad(deg: number): number {
    return deg * (Math.PI / 180);
  }

  public static rad2deg(rad: number): number {
    return rad * (180 / Math.PI);
  }

  public static buildPolygonFromZoneModel(
    zoneShapePointsArray: ShapePointViewModel[],
    mapObj: google.maps.Map
  ): google.maps.Polygon {
    const path: google.maps.LatLng[] = [];
    zoneShapePointsArray.forEach((point) => {
      const latLng = new google.maps.LatLng(point.latitude, point.longitude);
      path.push(latLng);
    });

    const polygon = new google.maps.Polygon({
      paths: path,
      strokeColor: ZonesAreasUtilies.ZONE_POLYGON_COLOR,
      strokeOpacity: 0.9,
      strokeWeight: 2,
      fillColor: ZonesAreasUtilies.ZONE_POLYGON_COLOR,
      fillOpacity: 0.2,
      clickable: true,
      map: mapObj,
      zIndex: 1,
    });

    return polygon;
  }

  public static buildPolygonFromAreaModel(
    areaShapePointsArray: ShapePointViewModel[],
    mapObj: google.maps.Map
  ): google.maps.Polygon {
    const path: google.maps.LatLng[] = [];
    areaShapePointsArray.forEach((point) => {
      const latLng = new google.maps.LatLng(point.latitude, point.longitude);
      path.push(latLng);
    });

    const polygon = new google.maps.Polygon({
      paths: path,
      strokeColor: ZonesAreasUtilies.AREA_POLYGON_COLOR,
      strokeOpacity: 0.9,
      strokeWeight: 2,
      fillColor: ZonesAreasUtilies.AREA_POLYGON_COLOR,
      fillOpacity: 0.2,
      clickable: true,
      map: mapObj,
      zIndex: 2,
    });

    return polygon;
  }

  public static getPolygonLatLngBounds(polygon: google.maps.Polygon): google.maps.LatLngBounds {
    const polyLatLngBounds = new google.maps.LatLngBounds();
    const polygonPath = polygon.getPath();

    if (polygonPath) {
      const vertexesArray = polygonPath.getArray();
      vertexesArray.forEach((latLng) => {
        polyLatLngBounds.extend(latLng);
      });
    }

    return polyLatLngBounds;
  }

  public static getPolygonsLatLngBounds(polygons: google.maps.Polygon[]): google.maps.LatLngBounds {
    const polyLatLngBounds = new google.maps.LatLngBounds();

    polygons.forEach((polygon) => {
      const polygonPath = polygon.getPath();

      if (polygonPath) {
        const vertexesArray = polygonPath.getArray();
        vertexesArray.forEach((latLng) => {
          polyLatLngBounds.extend(latLng);
        });
      }
    });

    return polyLatLngBounds;
  }

  public static fromShapePointToGoogleMapsLatLng(shapePoints: ShapePointViewModel[]): google.maps.LatLng[] {
    return shapePoints.map((p) => new google.maps.LatLng(p.latitude, p.longitude));
  }

  /**
   * Converts a `google.maps.LatLngBounds` object to a `GeoBounds` object.
   *
   * @param bounds - The Google Maps LatLngBounds instance to convert.
   * @returns A `GeoBounds` object containing the northeast and southwest coordinates.
   */
  public static toGeoBounds(bounds: google.maps.LatLngBounds): GeoBounds {
    return {
      northeast: {
        lat: bounds.getNorthEast().lat(),
        lng: bounds.getNorthEast().lng()
      },
      southwest: {
        lat: bounds.getSouthWest().lat(),
        lng: bounds.getSouthWest().lng()
      }
    };
  }

  public static toLatLngBounds(bounds: GeoBounds): google.maps.LatLngBounds {
    return new google.maps.LatLngBounds(
      new google.maps.LatLng(bounds.southwest.lat, bounds.southwest.lng),
      new google.maps.LatLng(bounds.northeast.lat, bounds.northeast.lng)
    );
  }

  /**
   * Calculates the geographic center point of the given bounds.
   *
   * @param bounds - The geographical bounds defined by southwest and northeast coordinates.
   * @returns The center point as a `GeoPoint` object containing latitude and longitude.
   */
  static calculateBoundsCenter(bounds: GeoBounds): GeoPoint {
    const latLngBounds = this.toLatLngBounds(bounds);

    const center = latLngBounds.getCenter();
    return {
      lat: center.lat(),
      lng: center.lng()
    };
  }

  /**
   * Computes the aspect ratio (width divided by height) of a rectangular area defined by map bounds.
   *
   * The width is calculated as the distance between the northwest (NW) and northeast (NE) corners,
   * and the height is calculated as the distance between the northwest (NW) and southwest (SW) corners.
   *
   * @param bounds - The map bounds object.
   * @returns The aspect ratio of the bounds (width / height).
   */
  static computeAspectRatio(bounds: google.maps.LatLngBounds): number {
    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();
    const nw = new google.maps.LatLng(ne.lat(), sw.lng());
    const se = new google.maps.LatLng(sw.lat(), ne.lng());

    // Width: distance between NW and NE;
    const width = google.maps.geometry.spherical.computeDistanceBetween(nw, ne);

    // Height: distance between NW and SW
    const height = google.maps.geometry.spherical.computeDistanceBetween(nw, sw);

    return width / height;
  }


  /**
   * Repositions a Google Maps rectangle to a new center position while maintaining its size and shape.
   *
   * @param rectangle - The Google Maps Rectangle object to be repositioned
   * @param newCenter - The new center coordinates (LatLng) where the rectangle should be positioned
   * @returns The new LatLngBounds of the repositioned rectangle
   */
  static repositionRectangle(rectangle: google.maps.Rectangle, newCenter: google.maps.LatLng): google.maps.LatLngBounds {
    const currentBounds = rectangle.getBounds();
    const currentCenter = currentBounds.getCenter();

    // Calculate offset
    const latOffset = newCenter.lat() - currentCenter.lat();
    const lngOffset = newCenter.lng() - currentCenter.lng();

    // Apply offset to bounds
    const sw = currentBounds.getSouthWest();
    const ne = currentBounds.getNorthEast();

    const newBounds = new google.maps.LatLngBounds(
      new google.maps.LatLng(sw.lat() + latOffset, sw.lng() + lngOffset),
      new google.maps.LatLng(ne.lat() + latOffset, ne.lng() + lngOffset)
    );

    rectangle.setBounds(newBounds);

    return newBounds;
  }

  /**
   * Calculates the area of a geographic bounds rectangle in square kilometers.
   *
   * This method computes the area by calculating the width and height of the bounds
   * using Google Maps spherical geometry calculations, then multiplying them together.
   * The calculation assumes the bounds form a rectangular area on the Earth's surface.
   *
   * @param bounds - The geographic bounds object containing northeast and southwest coordinates
   * @param bounds.northeast - The northeast corner coordinates with lat and lng properties
   * @param bounds.southwest - The southwest corner coordinates with lat and lng properties
   * @returns The area of the bounds in square kilometers
   */
  static calculateBoundsAreaKm2(bounds: GeoBounds): number {
    if (!bounds) {
      return 0;
    }

    const { northeast, southwest } = bounds;

    const swLatLng = new google.maps.LatLng(southwest.lat, southwest.lng);
    const neLatLng = new google.maps.LatLng(northeast.lat, northeast.lng);
    const nwLatLng = new google.maps.LatLng(northeast.lat, southwest.lng);
    const seLatLng = new google.maps.LatLng(southwest.lat, northeast.lng);

    // Calculate distances in meters using Google Maps geometry library
    const widthMeters = google.maps.geometry.spherical.computeDistanceBetween(swLatLng, seLatLng);
    const heightMeters = google.maps.geometry.spherical.computeDistanceBetween(swLatLng, nwLatLng);

    // Convert to kilometers
    const widthKm = widthMeters / 1000;
    const heightKm = heightMeters / 1000;

    return widthKm * heightKm;
  }

  /**
   * Calculates the geographical bounds (LatLngBounds) for a given map tile coordinate.
   *
   * This method converts tile coordinates (x, y) at a specific zoom level into
   * latitude and longitude boundaries that define the rectangular area covered by that tile.
   * Uses Web Mercator projection formulas to perform the coordinate transformation.
   *
   * @param coord - The tile coordinate as a Google Maps Point (x, y)
   * @param zoom - The zoom level of the map tile
   * @returns The geographical bounds of the tile as LatLngBounds, or null if coordinates cannot be normalized
   */
  static getTileLatLngBounds(coord: google.maps.Point, zoom: number): google.maps.LatLngBounds | null {
    const norm = this.normalizeTileCoord(coord, zoom);
    if (!norm) {
      return null;
    }

    const { x, y } = norm;

    const tileXToLon = (x: number, z: number): number => (x / Math.pow(2, z)) * 360 - 180;
    const tileYToLat = (y: number, z: number): number => {
      const n = Math.pow(2, z);
      const t = Math.PI - (2 * Math.PI * y) / n;

      return (180 / Math.PI) * Math.atan(Math.sinh(t));
    };

    const west = tileXToLon(x, zoom);
    const east = tileXToLon(x + 1, zoom);
    const north = tileYToLat(y, zoom);
    const south = tileYToLat(y + 1, zoom);

    return new google.maps.LatLngBounds(
      new google.maps.LatLng(south, west),
      new google.maps.LatLng(north, east)
    );
  }


  /**
   * Determines if two Google Maps LatLngBounds objects intersect with each other.
   *
   * This method first attempts to use the native `intersects` method if available.
   * If not available, it falls back to a manual implementation that checks if any
   * corner of one bounds is contained within the other bounds.
   *
   * @param a - The first LatLngBounds object to check for intersection
   * @param b - The second LatLngBounds object to check for intersection
   * @returns True if the bounds intersect, false otherwise
   */
  static boundsIntersects(a: google.maps.LatLngBounds, b: google.maps.LatLngBounds): boolean {
    if (typeof a.intersects === "function") {
      return a.intersects(b);
    }

    // Fallback: check corner inclusion either way
    const aSW = a.getSouthWest(), aNE = a.getNorthEast();
    const bSW = b.getSouthWest(), bNE = b.getNorthEast();
    const aNW = new google.maps.LatLng(aNE.lat(), aSW.lng());
    const aSE = new google.maps.LatLng(aSW.lat(), aNE.lng());
    const bNW = new google.maps.LatLng(bNE.lat(), bSW.lng());
    const bSE = new google.maps.LatLng(bSW.lat(), bNE.lng());

    const cornersA = [aSW, aSE, aNE, aNW];
    const cornersB = [bSW, bSE, bNE, bNW];

    const anyACornerInB = cornersA.some((c) => b.contains(c));
    const anyBCornerInA = cornersB.some((c) => a.contains(c));

    return anyACornerInB || anyBCornerInA;
  }


  /**
   * Determines if one LatLngBounds completely contains another LatLngBounds.
   *
   * @param outer - The outer bounds that should contain the inner bounds
   * @param inner - The inner bounds to check if it's fully contained within the outer bounds
   * @returns True if the outer bounds fully contains the inner bounds, false otherwise
   */
  static boundsFullyContains(outer: google.maps.LatLngBounds, inner: google.maps.LatLngBounds): boolean {
    const sw = inner.getSouthWest();
    const ne = inner.getNorthEast();

    return outer.contains(sw) && outer.contains(ne);
  }

  /**
   * Calculates the width and height of a Google Maps bounds in meters.
   *
   * This method computes the physical dimensions of a rectangular area defined by
   * latitude and longitude bounds using spherical geometry calculations.
   *
   * @param bounds - The Google Maps LatLngBounds object representing the rectangular area
   * @returns An object containing the width and height in meters:
   *   width - The horizontal distance between the western and eastern boundaries in meters
   *   height - The vertical distance between the southern and northern boundaries in meters
   */
  static boundsSizeMeters(bounds: google.maps.LatLngBounds): { width: number; height: number } {
    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();
    const se = new google.maps.LatLng(sw.lat(), ne.lng());
    const nw = new google.maps.LatLng(ne.lat(), sw.lng());

    const width = google.maps.geometry.spherical.computeDistanceBetween(sw, se);
    const height = google.maps.geometry.spherical.computeDistanceBetween(sw, nw);

    return { width, height };
  }


  private static normalizeTileCoord(coord: google.maps.Point, zoom: number): { x: number; y: number } | null {
    const n = 1 << zoom;
    const y = coord.y;
    if (y < 0 || y >= n) {
      return null; // outside vertical range
    }
    // wrap x into [0, n)
    const x = ((coord.x % n) + n) % n;

    return { x, y };
  }
}
